/**
 * Single-source-of-truth port assignments for local dev. Re-exported from
 * the root `ports.json` so every consumer (Vite configs, Playwright,
 * Firebase init, the Functions emulator-host detection, scripts) imports
 * from here instead of hardcoding numbers.
 *
 * Editing the values in this module is forbidden — update `ports.json`
 * at the repo root and `scripts/validate-config.mjs` catches any
 * generated config (firebase.json, docker-compose.yml, .env.local) that
 * drifts.
 */
import portsJson from '../../../../ports.json' with { type: 'json' };
import { z } from 'zod';
const PortEntry = z.object({
    port: z.number().int().min(1).max(65535),
    service: z.enum(['vite', 'firebase-emulator', 'docker', 'external']),
    purpose: z.string(),
});
const PortsConfig = z.object({
    version: z.string(),
    local: z.record(z.string(), PortEntry),
});
const parsed = PortsConfig.parse(portsJson);
/**
 * The full local-dev port map. Keys are stable service names; values
 * are the integer port. Useful for code paths that want to look up
 * "what port does the firestore emulator listen on?" without hardcoding
 * `8085`.
 */
export const LOCAL_PORTS = Object.freeze(Object.fromEntries(Object.entries(parsed.local).map(([name, entry]) => [name, entry.port])));
/** Full entry (port + service + purpose) for advanced consumers. */
export const LOCAL_PORT_ENTRIES = Object.freeze(parsed.local);
/**
 * Convenience: build a `127.0.0.1:<port>` host string for a service.
 * Useful for `FIRESTORE_EMULATOR_HOST` etc. (Note: 127.0.0.1, not
 * localhost — IPv4 explicit avoids IPv6 fallthrough on macOS.)
 */
export function localHost(service) {
    return `127.0.0.1:${LOCAL_PORTS[service]}`;
}
/** Same but with the `http://` scheme prefix. */
export function localUrl(service) {
    return `http://${localHost(service)}`;
}
